// src/components/StudentLoanInputs.js
import React from 'react';
import { Card, Checkbox, Row, Col, Tooltip, Typography, Collapse, Space } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { STUDENT_LOAN_PLAN_NAMES, TAX_YEARS } from '../constants/taxConstants';
import StudentLoanHelp from './StudentLoanHelp';

const { Text } = Typography;
const { Panel } = Collapse;

const StudentLoanInputs = ({ selectedPlans = [], onChange, selectedTaxYear = "2024/2025" }) => {
  const handlePlanChange = (planType, checked) => {
    if (checked) {
      onChange([...selectedPlans, planType]);
    } else {
      onChange(selectedPlans.filter(plan => plan !== planType));
    }
  };

  // Get threshold values for the selected tax year
  const getPlanThresholds = () => {
    const taxYearData = TAX_YEARS[selectedTaxYear];
    if (!taxYearData || !taxYearData.STUDENT_LOANS) {
      return {
        PLAN_1: '£22,095',
        PLAN_2: '£27,295',
        PLAN_4: '£31,395',
        PLAN_5: '£25,000',
        POSTGRAD: '£21,000'
      };
    }
    
    return {
      PLAN_1: formatCurrency(taxYearData.STUDENT_LOANS.PLAN_1.THRESHOLD),
      PLAN_2: formatCurrency(taxYearData.STUDENT_LOANS.PLAN_2.THRESHOLD),
      PLAN_4: formatCurrency(taxYearData.STUDENT_LOANS.PLAN_4.THRESHOLD),
      PLAN_5: formatCurrency(taxYearData.STUDENT_LOANS.PLAN_5.THRESHOLD),
      POSTGRAD: formatCurrency(taxYearData.STUDENT_LOANS.POSTGRAD.THRESHOLD)
    };
  };

  // Format the amount as currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', { 
      style: 'currency', 
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Get threshold values for the current tax year
  const planThresholds = getPlanThresholds();

  return (
    <Card 
      title={
        <Space>
          <span>Student Loan Repayments</span>
          {selectedPlans.length > 0 && (
            <span style={{ 
              backgroundColor: '#1890ff', 
              color: 'white', 
              borderRadius: '50%', 
              padding: '2px 8px',
              fontSize: '12px'
            }}>
              {selectedPlans.length}
            </span>
          )}
        </Space>
      } 
      style={{ marginBottom: 20 }}
    >
      <Collapse defaultActiveKey={selectedPlans.length > 0 ? ['1'] : []}>
        <Panel 
          header={
            <Text>
              {selectedPlans.length > 0 
                ? `${selectedPlans.length} Student Loan Plan${selectedPlans.length > 1 ? 's' : ''} Selected` 
                : "Click to select your Student Loan Plans"}
            </Text>
          } 
          key="1"
        >
          <Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
            Select all student loan plans that apply to you. Multiple plans can be selected if applicable.
          </Text>
          
          <Row gutter={[16, 16]}>
            {Object.entries(STUDENT_LOAN_PLAN_NAMES).map(([planType, planName]) => (
              <Col xs={24} sm={12} key={planType}>
                <Checkbox 
                  checked={selectedPlans.includes(planType)}
                  onChange={(e) => handlePlanChange(planType, e.target.checked)}
                >
                  {planName}
                  <Tooltip title={`Repayment threshold: ${planThresholds[planType]} per year (${selectedTaxYear}). Rate: 9% of income above threshold (6% for Postgraduate loans).`}>
                    <InfoCircleOutlined style={{ marginLeft: 8 }} />
                  </Tooltip>
                </Checkbox>
              </Col>
            ))}
          </Row>
          
          <Text type="secondary" style={{ marginTop: 16, display: 'block' }}>
            Student loan repayments are calculated as a percentage of your income that exceeds the repayment threshold.
            They are deducted through PAYE alongside income tax and National Insurance.
          </Text>
          
          {/* Help section - pass the selected tax year */}
          <StudentLoanHelp selectedTaxYear={selectedTaxYear} />
        </Panel>
      </Collapse>
    </Card>
  );
};

export default StudentLoanInputs;